import { createSelector } from 'reselect';
import { getLatestMasterExecutionIntervals } from './getLatestMasterExecutionIntervals';
import { getLastNonIdleExecutionInterval } from './getLastNonIdleExecutionInterval';

/**
 * Selector that returns all non-idle execution intervals sorted by start time (newest first).
 * Combines master execution intervals with the last non-idle interval, filters for ACTIVE/UNAVAILABLE
 * statuses, and removes duplicates by grouping on start time.
 */
const getLatestNonIdleIntervals = createSelector(
  [getLatestMasterExecutionIntervals, getLastNonIdleExecutionInterval],
  (latestMasterExecutionIntervals, lastNonIdleInterval) => {
    // If no master intervals available, return just the last non-idle interval
    if (
      !latestMasterExecutionIntervals ||
      !Array.isArray(latestMasterExecutionIntervals)
    ) {
      if (lastNonIdleInterval) {
        return [lastNonIdleInterval];
      }
      return [];
    }

    // Combine all intervals and filter for non-idle statuses (ACTIVE/UNAVAILABLE)
    const executionIntervals = [
      ...latestMasterExecutionIntervals,
      lastNonIdleInterval,
    ]
      ?.filter(Boolean)
      ?.filter((interval) => {
        return ['ACTIVE', 'UNAVAILABLE'].includes(interval?.label);
      });

    // Group intervals by start time to remove duplicates
    const groupedExecutionIntervals = executionIntervals?.reduce(
      (acc, interval) => {
        return {
          ...acc,
          [interval.start]: interval,
        };
      },
      {}
    );

    // Sort by start time (newest first) and return as array
    return Object.keys(groupedExecutionIntervals)
      .sort((a, b) => {
        const dateA = new Date(a);
        const dateB = new Date(b);
        return dateB.getTime() - dateA.getTime();
      })
      .map((key) => {
        return groupedExecutionIntervals[key];
      });
  }
);

/**
 * Selector that returns the most recent non-idle execution interval.
 * Returns the first (newest) interval from the sorted list of non-idle intervals.
 */
const getLatestNonIdleInterval = createSelector(
  [getLatestNonIdleIntervals],
  (latestNonIdleIntervals) => {
    return latestNonIdleIntervals?.[0];
  }
);

/**
 * Selector that returns the most recent non-idle execution interval that is not currently idle.
 * Returns the first (newest) interval from the sorted list of non-idle intervals that has an end time.
 */
const getLatestNonCurrentIdleInterval = createSelector(
  [getLatestNonIdleIntervals],
  (latestNonIdleIntervals) => {
    return latestNonIdleIntervals?.find((interval) => {
      return interval.end != null;
    });
  }
);

export {
  getLatestNonIdleIntervals,
  getLatestNonIdleInterval,
  getLatestNonCurrentIdleInterval,
};
